import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import type { CheckoutInterval } from '@klab-berlin/api-sdk/lib/types/requests/Billing';
import { borderRadius, customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';

export interface StripeCheckoutSummaryProps {
  planLabel: string;
  /** Current billing interval — drives the dropdown's selected value. */
  interval: CheckoutInterval;
  /** Called when the user picks a different value from the dropdown. */
  onIntervalChange: (next: CheckoutInterval) => void;
  /** Current value of the promo-code input (controlled). */
  promoInput: string;
  /** Called on every keystroke; updates the controlled input. */
  onPromoInputChange: (value: string) => void;
  /** Called when the user clicks "Einlösen". */
  onApplyPromo: () => void;
  /**
   * The single promo code currently in effect — either a manually-applied
   * code or the Ref-Rabatt code. Empty string when nothing is applied. When
   * set, the summary renders it as a removable chip instead of the input.
   */
  appliedPromoLabel: string;
  /** Called when the user clicks the X on the applied-promo chip. */
  onRemoveAppliedPromo: () => void;
  /** When true, render the inline invalid-promo error under the input. */
  promoError: boolean;
  /** Formatted price reduction (e.g. "-137,40 €") shown next to the applied-promo chip. */
  promoReduction?: string;
  /**
   * Optional override for the inline invalid-promo error copy. When omitted
   * the summary renders its built-in generic message (`The code is invalid…`).
   * The resubscribe flow uses this to render reason-specific messages —
   * "first-time customers only", "already redeemed", etc. — driven by the
   * kimuno error response's `reason` discriminator.
   */
  promoErrorMessage?: string;
  /** True while a session re-creation (promo or interval change) is in flight. */
  applying: boolean;
  /**
   * Optional node rendered between the package header (title + description)
   * and the rest of the controls. Used to surface the live Stripe order
   * total + discount breakdown right under the product name.
   */
  pricePanel?: React.ReactNode;
  /**
   * Optional node rendered at the bottom of the summary, below the promo
   * controls. Used to surface the live Stripe price table (amount due today +
   * total over the contract term).
   */
  priceTable?: React.ReactNode;
  /**
   * Trial mode. Swaps the header for a "free trial" title + plan-name chip +
   * reminder copy, and replaces the promo-code row with the Ref-Rabatt toggle.
   */
  trial?: boolean;
  /** Trial length (days) shown in the trial header. Sourced from Stripe. */
  trialDays?: number;
  /** Ref-Rabatt toggle state. */
  refRabattOn?: boolean;
  onRefRabattChange?: (value: boolean) => void;
  /**
   * Gates the Ref-Rabatt toggle in both modes. In non-trial mode the parent
   * sets it when the checkout link carries the ref-rabatt code; in trial mode
   * it reflects plan eligibility (e.g. the AI plan has no Ref-Rabatt).
   */
  showRefRabattToggle?: boolean;
}

const INTERVAL_OPTIONS: ReadonlyArray<{ value: CheckoutInterval; labelKey: string }> = [
  { value: 'monthly', labelKey: 'Monthly' },
  { value: 'yearly', labelKey: 'Yearly' },
];

const StripeCheckoutSummary: React.FC<StripeCheckoutSummaryProps> = ({
  planLabel,
  interval,
  onIntervalChange,
  promoInput,
  onPromoInputChange,
  onApplyPromo,
  appliedPromoLabel,
  onRemoveAppliedPromo,
  promoError,
  promoReduction,
  promoErrorMessage,
  applying,
  pricePanel,
  priceTable,
  trial = false,
  trialDays,
  refRabattOn = false,
  onRefRabattChange,
  showRefRabattToggle = false,
}) => {
  const { t } = useTranslation();
  const { isLgScreen, isMdScreen } = useScreenSize();
  const isDesktopScreen = isLgScreen || isMdScreen;
  const hasAppliedPromo = appliedPromoLabel.length > 0;
  const inputDisabled = applying;
  // The promo input stays hidden behind an "Add discount code" trigger until
  // the user opens it.
  const [showPromoInput, setShowPromoInput] = useState(false);

  const refRabattToggle = (
    <FormControlLabel
      sx={{ margin: 0, gap: spaces.xs, width: 'fit-content' }}
      labelPlacement='start'
      control={
        <Switch
          checked={refRabattOn}
          onChange={(e) => onRefRabattChange?.(e.target.checked)}
          disabled={applying}
          size='medium'
          inputProps={
            { 'data-testid': 'stripe-checkout-ref-rabatt' } as React.InputHTMLAttributes<HTMLInputElement>
          }
        />
      }
      label={
        <Typography variant='body1' sx={{ color: '#493666' }}>
          {t('Ref-Rabatt')}
        </Typography>
      }
    />
  );

  return (
    <Box
      data-testid='stripe-checkout-summary'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.lg,
      }}
    >
      {trial ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.sm }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: spaces.xs,
            }}
          >
            <Typography variant='h4' sx={{ fontWeight: 700, color: palette.text.primary }}>
              {trialDays === undefined ? (
                <Skeleton variant='text' width={140} />
              ) : (
                t('{{days}}-day free trial', { days: trialDays })
              )}
            </Typography>
            <Chip
              label={refRabattOn ? t('For trainees') : planLabel}
              color='primary'
              variant='outlined'
              size='small'
              data-testid='stripe-checkout-trial-plan'
              sx={{ flexShrink: 0 }}
            />
          </Box>
          <Typography variant='body2' sx={{ color: palette.text.secondary }}>
            {t(
              'If you cancel during your trial period, you incur no costs.' +
                ' No worries: after 7 days we\'ll send you a reminder.',
            )}
          </Typography>
          {showRefRabattToggle && refRabattToggle}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.sm }}>
          <Typography variant='h4' sx={{ fontWeight: 700, color: palette.text.primary }}>
            {planLabel}
          </Typography>
          {showRefRabattToggle && refRabattToggle}
        </Box>
      )}

      <Box sx={{ opacity: applying ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        {pricePanel}
      </Box>

      <Table sx={{ opacity: applying ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        <TableBody>
          <TableRow>
            <TableCell sx={{ paddingX: 0 }}>
              <Typography variant='body2' id='stripe-checkout-interval-label'>
                {t('Payment rhythm')}
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                paddingX: 0,
                width: isDesktopScreen
                  ? customSizes.paymentRhythmSelect.width.desktop
                  : customSizes.paymentRhythmSelect.width.mobile,
              }}
            >
              <FormControl variant='outlined' size='small' fullWidth>
                <Select
                  data-testid='stripe-checkout-interval-select'
                  value={interval}
                  onChange={(e) => onIntervalChange(e.target.value as CheckoutInterval)}
                  disabled={applying}
                  inputProps={{ 'aria-labelledby': 'stripe-checkout-interval-label' }}
                  sx={{ borderRadius: borderRadius.sm }}
                >
                  {INTERVAL_OPTIONS.map(({ value, labelKey }) => (
                    <MenuItem key={value} value={value}>
                      {t(labelKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TableCell>
          </TableRow>

          {hasAppliedPromo && (
            <TableRow>
              <TableCell sx={{ paddingX: 0 }} colSpan={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: spaces.sm }}>
                    <Typography variant='body2' sx={{ color: palette.text.secondary }}>
                      {t('Discount')}
                    </Typography>
                    <Chip
                      label={appliedPromoLabel}
                      onDelete={onRemoveAppliedPromo}
                      disabled={applying}
                      deleteIcon={<CancelIcon fontSize='small' data-testid='stripe-checkout-remove-promo' />}
                      data-testid='stripe-checkout-applied-promo'
                      sx={{ '& .MuiChip-label': { color: palette.text.primary, fontWeight: 500 } }}
                    />
                  </Box>
                  {promoReduction && (
                    <Typography variant='body2' data-testid='stripe-checkout-promo-reduction'>
                      {promoReduction}
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          )}

          {!hasAppliedPromo && (
            <TableRow>
              <TableCell sx={{ paddingX: 0 }} colSpan={2}>
                {!(showPromoInput || promoError) ? (
                  <Button
                    type='button'
                    variant='text'
                    disableRipple
                    onClick={() => setShowPromoInput(true)}
                    data-testid='stripe-checkout-promo-reveal'
                    sx={{
                      padding: 0,
                      minWidth: 0,
                      width: 'fit-content',
                      textTransform: 'none',
                      fontWeight: 300,
                      fontSize: '0.875rem',
                      color: palette.text.secondary,
                      '&:hover': { backgroundColor: 'transparent' },
                    }}
                  >
                    {t('Add discount code')}
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: spaces.xs }}>
                    <TextField
                      variant='outlined'
                      placeholder={t('Enter your code here')}
                      value={promoInput}
                      onChange={(e) => onPromoInputChange(e.target.value)}
                      disabled={inputDisabled}
                      error={promoError}
                      size='small'
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: borderRadius.xs,
                        },
                      }}
                      inputProps={{ maxLength: 64, 'data-testid': 'stripe-checkout-promocode' }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !inputDisabled && promoInput.trim().length > 0) {
                          e.preventDefault();
                          onApplyPromo();
                        }
                      }}
                    />
                    <Button
                      type='button'
                      variant='text'
                      size='large'
                      onClick={onApplyPromo}
                      disabled={inputDisabled || promoInput.trim().length === 0}
                      data-testid='stripe-checkout-promo-apply'
                      sx={{
                        color: promoError ? palette.error.main : palette.secondary.main,
                        alignSelf: 'baseline',
                      }}
                    >
                      {t('Redeem')}
                    </Button>
                  </Box>
                )}
                {promoError && (
                  <FormHelperText
                    error
                    data-testid='stripe-checkout-promo-error'
                    sx={{ marginTop: spaces.xs, marginLeft: 0 }}
                  >
                    {promoErrorMessage ??
                    t('The code is invalid or does not exist — please try entering your code again')}
                  </FormHelperText>
                )}
              </TableCell>
            </TableRow>
          )}

          {priceTable}
        </TableBody>
      </Table>
    </Box>
  );
};

export default StripeCheckoutSummary;
